import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faClockRotateLeft, faDownload, faMoneyCheckDollar, faPen, faPlus, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useConti } from '../../components/conti/ContiLayout';
import { IconaTonda, dataIt, euro } from '../../components/conti/contiTheme';
import {
  creaFinanziamento, getFinanziamenti, getRapporti, getRateFinanziamento, importaFinanziamenti, impostaResiduo,
  messaggioErrore, modificaFinanziamento, registraRata,
} from '../../services/contiService';

const TIPI = {
  finanziamento: { l: 'Finanziamento', icona: 'bank', colore: '#3b5bdb' },
  cessione_quinto: { l: 'Cessione del quinto', icona: 'percent', colore: '#7048e8' },
  carta_credito: { l: 'Carta di credito', icona: 'card', colore: '#e8590c' },
  debito_privato: { l: 'Debito tra privati', icona: 'coins', colore: '#868e96' },
  altro: { l: 'Altro', icona: 'wallet', colore: '#495057' },
};
const INTESTATARI = ['Giorgio', 'Francesca', 'Famiglia', 'Azienda'];
const oggi = () => new Date().toISOString().slice(0, 10);
const VUOTO = {
  nome: '', tipo: 'finanziamento', creditore: '', intestatario: '', importo_originario: '', rata_mensile: '', giorno_rata: '',
  data_inizio: '', data_fine: '', tan: '', note: '', residuo: '', data_residuo: oggi(),
};
const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(String(v).replace(',', '.')));

const fineStimata = (f) => {
  if (f.data_fine) return dataIt(f.data_fine);
  if (f.rate_rimanenti) {
    const d = new Date();
    d.setMonth(d.getMonth() + f.rate_rimanenti);
    return `circa ${d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`;
  }
  return '—';
};

const Finanziamenti = () => {
  const { scope, tema } = useConti();
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(true);
  const [rapporti, setRapporti] = useState([]);
  const [mostraArchiviati, setMostraArchiviati] = useState(false);
  const [form, setForm] = useState(null);
  const [residuo, setResiduo] = useState(null);
  const [rata, setRata] = useState(null);
  const [storico, setStorico] = useState(null);
  const [lavoro, setLavoro] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setDati(await getFinanziamenti(scope));
    } catch (e) {
      toast.error(messaggioErrore(e, 'Impossibile caricare i finanziamenti'));
    } finally {
      setCaricamento(false);
    }
  }, [scope]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    getRapporti(scope).then((r) => setRapporti(r.rapporti.filter((x) => x.attivo))).catch(() => setRapporti([]));
  }, [scope]);

  const esegui = async (azione, ok) => {
    setLavoro(true);
    try {
      await azione();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      toast.error(messaggioErrore(e));
      return false;
    } finally {
      setLavoro(false);
    }
  };

  const salvaForm = async (e) => {
    e.preventDefault();
    const payload = {
      nome: form.nome, tipo: form.tipo, creditore: form.creditore || null, intestatario: form.intestatario || null,
      importo_originario: num(form.importo_originario), rata_mensile: num(form.rata_mensile),
      giorno_rata: form.giorno_rata === '' ? null : parseInt(form.giorno_rata, 10), data_inizio: form.data_inizio || null,
      data_fine: form.data_fine || null, tan: num(form.tan), note: form.note || null,
    };
    const fatto = await esegui(async () => {
      if (form.id) await modificaFinanziamento(form.id, payload);
      else await creaFinanziamento({ ...payload, scope, residuo: num(form.residuo), data_residuo: form.residuo === '' ? null : form.data_residuo });
    }, form.id ? 'Finanziamento aggiornato' : 'Finanziamento creato e collegato a Firefly');
    if (fatto) { setForm(null); carica(); }
  };

  const apriModifica = (f) => setForm({
    id: f.id, nome: f.nome, tipo: f.tipo, creditore: f.creditore || '', intestatario: f.intestatario || '',
    importo_originario: f.importo_originario ?? '', rata_mensile: f.rata_mensile ?? '', giorno_rata: f.giorno_rata ?? '',
    data_inizio: f.data_inizio || '', data_fine: f.data_fine || '', tan: f.tan ?? '', note: f.note || '',
  });

  const archivia = async (f) => {
    if (!window.confirm(f.attivo ? `Archiviare "${f.nome}"? Resta in Firefly con il suo storico.` : `Riattivare "${f.nome}"?`)) return;
    if (await esegui(() => modificaFinanziamento(f.id, { attivo: !f.attivo }))) carica();
  };

  const importa = async () => {
    if (!window.confirm('Creo le schede partendo dalle passività già presenti in Firefly. Procedo?')) return;
    setLavoro(true);
    try {
      const r = await importaFinanziamenti(scope);
      toast.success(r.nuovi ? `${r.nuovi} finanziamenti importati` : 'Nessuna passività nuova da importare');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setLavoro(false);
    }
  };

  const finanziamenti = (dati?.finanziamenti || []).filter((f) => mostraArchiviati || f.attivo);
  const bottone = { background: tema.colore, borderColor: tema.colore };

  return (
    <>
      <Row className="g-3 mb-3">
        {[
          ['Debito residuo totale', euro(dati?.totale_residuo), '#e03131', faScaleBalanced],
          ['Rate mensili', euro(dati?.totale_rate_mensili), '#e8590c', faMoneyCheckDollar],
          ['Finanziamenti attivi', (dati?.finanziamenti || []).filter((f) => f.attivo).length, tema.colore, faScaleBalanced],
        ].map(([t, v, c, ic]) => (
          <Col md={4} key={t}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', borderLeft: `6px solid ${c}` }}>
              <div className="text-muted" style={{ fontSize: 13 }}><FontAwesomeIcon icon={ic} className="me-2" style={{ color: c }} />{t}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c }}>{v}</div>
            </div>
          </Col>
        ))}
      </Row>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <Form.Check type="switch" id="mostra-arch-fin" label="Mostra archiviati" checked={mostraArchiviati} onChange={(e) => setMostraArchiviati(e.target.checked)} />
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={importa} disabled={lavoro}><FontAwesomeIcon icon={faDownload} className="me-2" />Importa da Firefly</Button>
          <Button style={bottone} onClick={() => setForm({ ...VUOTO, intestatario: scope === 'azienda' ? 'Azienda' : '' })}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />Nuovo finanziamento
          </Button>
        </div>
      </div>

      {dati && !dati.firefly_ok && <Alert variant="warning">Firefly non risponde: i debiti residui non sono disponibili in questo momento.</Alert>}
      {caricamento && <div className="text-center py-5"><Spinner animation="border" /></div>}
      {!caricamento && finanziamenti.length === 0 && (
        <div className="text-center text-muted py-5" style={{ border: '2px dashed #ced4da', borderRadius: 16 }}>
          Nessun finanziamento in {tema.nome}. Importa le passività già presenti in Firefly o creane una nuova.
        </div>
      )}

      <Row className="g-3">
        {finanziamenti.map((f) => {
          const t = TIPI[f.tipo] || TIPI.altro;
          return (
            <Col key={f.id} lg={6}>
              <div style={{ background: '#fff', borderRadius: 16, padding: 18, boxShadow: '0 2px 10px rgba(0,0,0,.07)', borderTop: `5px solid ${t.colore}`, opacity: f.attivo ? 1 : 0.55, height: '100%' }}>
                <div className="d-flex align-items-center gap-3">
                  <IconaTonda icona={t.icona} colore={t.colore} />
                  <div style={{ minWidth: 0 }} className="flex-grow-1">
                    <div style={{ fontWeight: 700, fontSize: 17 }} className="text-truncate">{f.nome}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {t.l}{f.creditore ? ` · ${f.creditore}` : ''}{f.intestatario ? ` · ${f.intestatario}` : ''}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-muted" style={{ fontSize: 12 }}>Debito residuo</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: (f.residuo || 0) > 0 ? '#e03131' : '#2f9e44' }}>{euro(f.residuo)}</div>
                  </div>
                </div>
                {f.pagato_perc !== null && (
                  <div className="mt-3">
                    <ProgressBar now={f.pagato_perc} variant="success" style={{ height: 8 }} />
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {f.pagato_perc}% rimborsato su {euro(f.importo_originario)}
                    </div>
                  </div>
                )}
                {(f.residuo === 0 || f.residuo === null) && f.attivo && (
                  <Alert variant="warning" className="py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>
                    Debito a 0 €: usa «Imposta residuo» per indicare quanto resta da pagare.
                  </Alert>
                )}
                <Row className="mt-3 g-2" style={{ fontSize: 13 }}>
                  <Col xs={6} md={3}><div className="text-muted">Rata mensile</div><strong>{euro(f.rata_mensile)}</strong></Col>
                  <Col xs={6} md={3}><div className="text-muted">Prossima rata</div><strong>{f.prossima_rata ? dataIt(f.prossima_rata) : '—'}</strong></Col>
                  <Col xs={6} md={3}><div className="text-muted">Rate rimanenti</div><strong>{f.rate_rimanenti ?? '—'}</strong></Col>
                  <Col xs={6} md={3}><div className="text-muted">Fine prevista</div><strong>{fineStimata(f)}</strong></Col>
                </Row>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button size="sm" style={bottone} onClick={() => setRata({
                    f, banca_id: '', data: oggi(), totale: f.rata_mensile ?? '', interessi: '', descrizione: '',
                  })}>Registra rata</Button>
                  <Button size="sm" variant="outline-primary" onClick={() => setResiduo({ f, importo: f.residuo ?? '', data: oggi() })}>Imposta residuo</Button>
                  <Button size="sm" variant="outline-secondary" onClick={async () => {
                    try { setStorico({ f, rate: (await getRateFinanziamento(f.id)) }); } catch (e) { toast.error(messaggioErrore(e)); }
                  }}><FontAwesomeIcon icon={faClockRotateLeft} className="me-1" />Storico</Button>
                  <Button size="sm" variant="light" onClick={() => apriModifica(f)}><FontAwesomeIcon icon={faPen} /></Button>
                  <Button size="sm" variant="light" onClick={() => archivia(f)} title={f.attivo ? 'Archivia' : 'Riattiva'}><FontAwesomeIcon icon={faBoxArchive} /></Button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* ---- Scheda finanziamento ---- */}
      <Modal show={!!form} onHide={() => setForm(null)} centered size="lg">
        {form && (
          <Form onSubmit={salvaForm}>
            <Modal.Header closeButton style={{ background: tema.morbido }}>
              <Modal.Title style={{ color: tema.scuro }}>{form.id ? 'Modifica finanziamento' : 'Nuovo finanziamento'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={8}><Form.Label>Nome</Form.Label><Form.Control required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="es. Prestito ING 5.000 €" /></Col>
                <Col md={4}><Form.Label>Tipo</Form.Label>
                  <Form.Select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    {Object.entries(TIPI).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </Form.Select></Col>
                <Col md={6}><Form.Label>Creditore</Form.Label><Form.Control value={form.creditore} onChange={(e) => setForm({ ...form, creditore: e.target.value })} placeholder="es. ING, Findomestic" /></Col>
                <Col md={6}><Form.Label>Intestatario</Form.Label>
                  <Form.Select value={form.intestatario} onChange={(e) => setForm({ ...form, intestatario: e.target.value })}>
                    <option value="">—</option>{INTESTATARI.map((i) => <option key={i} value={i}>{i}</option>)}
                  </Form.Select></Col>
                <Col md={4}><Form.Label>Importo originario (€)</Form.Label><Form.Control type="number" step="0.01" min="0" value={form.importo_originario} onChange={(e) => setForm({ ...form, importo_originario: e.target.value })} /></Col>
                <Col md={4}><Form.Label>Rata mensile (€)</Form.Label><Form.Control type="number" step="0.01" min="0" value={form.rata_mensile} onChange={(e) => setForm({ ...form, rata_mensile: e.target.value })} /></Col>
                <Col md={4}><Form.Label>Giorno della rata</Form.Label><Form.Control type="number" min="1" max="31" value={form.giorno_rata} onChange={(e) => setForm({ ...form, giorno_rata: e.target.value })} /></Col>
                <Col md={4}><Form.Label>Data inizio</Form.Label><Form.Control type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} /></Col>
                <Col md={4}><Form.Label>Data fine <span className="text-muted">(facoltativa)</span></Form.Label><Form.Control type="date" value={form.data_fine} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} /></Col>
                <Col md={4}><Form.Label>TAN (%)</Form.Label><Form.Control type="number" step="0.01" min="0" value={form.tan} onChange={(e) => setForm({ ...form, tan: e.target.value })} /></Col>
                {!form.id && (
                  <>
                    <Col md={6}><Form.Label>Debito residuo oggi (€) <span className="text-muted">(facoltativo)</span></Form.Label>
                      <Form.Control type="number" step="0.01" min="0" value={form.residuo} onChange={(e) => setForm({ ...form, residuo: e.target.value })} /></Col>
                    <Col md={6}><Form.Label>alla data</Form.Label><Form.Control type="date" value={form.data_residuo} onChange={(e) => setForm({ ...form, data_residuo: e.target.value })} /></Col>
                  </>
                )}
                <Col md={12}><Form.Label>Note</Form.Label><Form.Control as="textarea" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setForm(null)}>Annulla</Button>
              <Button type="submit" disabled={lavoro} style={bottone}>{lavoro ? 'Salvo…' : 'Salva'}</Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ---- Imposta residuo ---- */}
      <Modal show={!!residuo} onHide={() => setResiduo(null)} centered>
        {residuo && (
          <Form onSubmit={async (e) => {
            e.preventDefault();
            if (await esegui(() => impostaResiduo(residuo.f.id, { importo: num(residuo.importo), data: residuo.data }), 'Debito residuo aggiornato')) { setResiduo(null); carica(); }
          }}>
            <Modal.Header closeButton><Modal.Title>Imposta il debito residuo</Modal.Title></Modal.Header>
            <Modal.Body>
              <p className="text-muted" style={{ fontSize: 14 }}>
                <strong>{residuo.f.nome}</strong>: indica quanto resta da pagare alla data scelta (lo trovi sull'ultimo estratto del finanziamento).
                Le rate registrate dopo quella data lo riducono.
              </p>
              <Row className="g-3">
                <Col><Form.Label>Debito residuo (€)</Form.Label><Form.Control required type="number" step="0.01" min="0" autoFocus value={residuo.importo} onChange={(e) => setResiduo({ ...residuo, importo: e.target.value })} /></Col>
                <Col><Form.Label>alla data</Form.Label><Form.Control required type="date" value={residuo.data} onChange={(e) => setResiduo({ ...residuo, data: e.target.value })} /></Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setResiduo(null)}>Annulla</Button>
              <Button type="submit" disabled={lavoro} style={bottone}>Salva</Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ---- Registra rata ---- */}
      <Modal show={!!rata} onHide={() => setRata(null)} centered>
        {rata && (() => {
          const totale = num(rata.totale) || 0;
          const interessi = num(rata.interessi) || 0;
          const capitale = Math.round((totale - interessi) * 100) / 100;
          return (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              if (await esegui(() => registraRata(rata.f.id, {
                banca_id: Number(rata.banca_id), data: rata.data, capitale, interessi: interessi || null, descrizione: rata.descrizione || null,
              }), 'Rata registrata su Firefly')) { setRata(null); carica(); }
            }}>
              <Modal.Header closeButton><Modal.Title>Registra rata</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted" style={{ fontSize: 14 }}><strong>{rata.f.nome}</strong></p>
                <Row className="g-3">
                  <Col md={12}><Form.Label>Pagata dal rapporto</Form.Label>
                    <Form.Select required value={rata.banca_id} onChange={(e) => setRata({ ...rata, banca_id: e.target.value })}>
                      <option value="">— scegli —</option>
                      {rapporti.map((r) => <option key={r.id} value={r.id}>{r.denominazione}</option>)}
                    </Form.Select></Col>
                  <Col md={4}><Form.Label>Data</Form.Label><Form.Control required type="date" value={rata.data} onChange={(e) => setRata({ ...rata, data: e.target.value })} /></Col>
                  <Col md={4}><Form.Label>Importo rata (€)</Form.Label><Form.Control required type="number" step="0.01" min="0.01" value={rata.totale} onChange={(e) => setRata({ ...rata, totale: e.target.value })} /></Col>
                  <Col md={4}><Form.Label>di cui interessi (€)</Form.Label><Form.Control type="number" step="0.01" min="0" value={rata.interessi} onChange={(e) => setRata({ ...rata, interessi: e.target.value })} /></Col>
                  <Col md={12}><Form.Label>Descrizione <span className="text-muted">(facoltativa)</span></Form.Label><Form.Control value={rata.descrizione} onChange={(e) => setRata({ ...rata, descrizione: e.target.value })} /></Col>
                </Row>
                <Alert variant="info" className="mt-3 mb-0" style={{ fontSize: 13 }}>
                  Quota capitale <strong>{euro(capitale > 0 ? capitale : 0)}</strong> (riduce il debito)
                  {interessi > 0 ? <> + interessi <strong>{euro(interessi)}</strong> (spesa)</> : null}.
                  Se importi l'estratto conto, ricorda di ignorare la riga di questa rata per non contarla due volte.
                </Alert>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setRata(null)}>Annulla</Button>
                <Button type="submit" disabled={lavoro || capitale <= 0} style={bottone}>{lavoro ? 'Registro…' : 'Registra'}</Button>
              </Modal.Footer>
            </Form>
          );
        })()}
      </Modal>

      {/* ---- Storico ---- */}
      <Modal show={!!storico} onHide={() => setStorico(null)} centered>
        {storico && (
          <>
            <Modal.Header closeButton><Modal.Title>Storico · {storico.f.nome}</Modal.Title></Modal.Header>
            <Modal.Body>
              {storico.rate.length === 0 ? <div className="text-muted">Nessun movimento registrato.</div> : (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {storico.rate.map((r, i) => (
                      <tr key={i}>
                        <td>{dataIt(r.data)}</td>
                        <td>{r.tipo === 'opening balance' ? 'Debito impostato' : r.descrizione}</td>
                        <td className="text-end" style={{ fontWeight: 600 }}>{euro(r.importo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
};

export default Finanziamenti;
